import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowDown,
  ArrowUp,
  BarChart3,
  Bell,
  ChevronDown,
  ClipboardList,
  Clock,
  Crown,
  Database,
  FileSpreadsheet,
  HelpCircle,
  LogOut,
  Minus,
  Rocket,
  Search,
  Settings,
  Target,
  Trophy,
  Users,
  Zap,
} from 'lucide-react';
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { BrandLockup } from '../components/brand/DatabinexMark';
import { TeamAvatar } from '../components/team/Avatar';
import { TEAM_QUESTION_COUNT } from '../lib/arena-rules';
import { formatFaNumber } from '../lib/format';
import { averageAnswerMs, formatDuration, teamPerformanceLabel } from '../lib/team-performance';
import type { ArenaEvent, TeamPublic } from '../types/arena';

export function LeaderboardPage({ event, teams }: { event: ArenaEvent; teams: TeamPublic[] }) {
  const previousRanks = useRef(new Map<string, number>());
  const currentRanks = new Map(teams.map((team, index) => [team.id, index + 1]));

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      previousRanks.current = currentRanks;
    }, 900);
    return () => window.clearTimeout(timeout);
  });

  if (event.status === 'finished') return <FinalLeaderboard teams={teams} />;

  const updatedAt = new Date().toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <main className="min-h-screen overflow-hidden bg-[#172033] text-ink">
      <div className="grid min-h-screen lg:grid-cols-[1fr_280px]" dir="ltr">
        <section className="p-5 lg:p-8" dir="rtl">
          <div className="flex min-h-[calc(100vh-40px)] flex-col overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_28px_90px_rgba(3,9,23,.22)]">
            <header className="flex min-h-[150px] items-start justify-between gap-6 border-b border-slate-200 px-7 py-7">
              <div className="flex items-center gap-4">
                <button type="button" className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm">
                  خروجی اکسل
                  <FileSpreadsheet className="size-4" />
                </button>
                <button type="button" className="inline-flex h-11 items-center gap-2 rounded-xl bg-white px-4 text-sm font-bold text-slate-600">
                  راهنما
                  <HelpCircle className="size-4" />
                </button>
              </div>
              <div className="text-right">
                <h1 className="text-3xl font-black tracking-tight">جدول رتبه‌بندی زنده</h1>
                <p className="mt-2 text-sm font-medium text-slate-500">{displayEventName(event.name)}</p>
                <div className="mt-9 flex flex-wrap items-center justify-end gap-4 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-2 font-black text-emerald-600">
                    <span className="size-3 rounded-full bg-emerald-500 shadow-[0_0_14px_rgba(34,197,94,.7)]" />
                    زنده
                  </span>
                  <span>آخرین به‌روزرسانی: {updatedAt}</span>
                  <span>به‌روزرسانی خودکار هر</span>
                  <button type="button" className="inline-flex h-10 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 font-bold text-slate-700 shadow-sm">
                    ۱۵ ثانیه
                    <ChevronDown className="size-4" />
                  </button>
                </div>
              </div>
            </header>

            <div className="border-b border-slate-200 px-7 py-6">
              <div className="grid gap-4 xl:grid-cols-[1fr_250px_250px_230px]">
                <label className="flex h-12 min-w-0 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 text-slate-400 shadow-sm">
                  <Search className="size-5" />
                  <span className="text-sm">جستجو در تیم‌ها...</span>
                </label>
                <FilterButton icon={<Trophy className="size-4" />} label="مرتب‌سازی: امتیاز (نزولی)" />
                <FilterButton icon={<Zap className="size-4 text-amber-500" />} label="مرتب‌سازی: سرعت (صعودی)" />
                <FilterButton icon={<Users className="size-4" />} label="همه تیم‌ها" />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden px-7 pb-4 pt-5">
              <div className="overflow-hidden rounded-[14px] border border-slate-200 bg-white">
                <div className="grid min-w-[1040px] grid-cols-[72px_1.3fr_130px_120px_120px_150px_150px_120px] items-center border-b border-slate-200 bg-slate-50 px-5 py-4 text-xs font-black text-slate-500">
                  <span>رتبه</span>
                  <span>تیم</span>
                  <span>امتیاز کل</span>
                  <span>سؤالات حل‌شده</span>
                  <span>دقت پاسخ</span>
                  <span>میانگین سرعت پاسخ</span>
                  <span>آخرین فعالیت</span>
                  <span>تغییر رتبه</span>
                </div>
                <div className="overflow-x-auto">
                  <motion.div layout>
                    <AnimatePresence initial={false}>
                      {teams.slice(0, 10).map((team, index) => {
                        const rank = index + 1;
                        const oldRank = previousRanks.current.get(team.id) ?? team.previousRank ?? rank;
                        const delta = oldRank - rank;
                        const accuracy = Math.round((team.correctCount / Math.max(1, team.submittedCount)) * 100);
                        const averageMs = averageAnswerMs(team);
                        return (
                          <motion.div
                            layout
                            key={team.id}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ layout: { type: 'spring', stiffness: 360, damping: 32 } }}
                            className={`grid min-h-[72px] min-w-[1040px] grid-cols-[72px_1.3fr_130px_120px_120px_150px_150px_120px] items-center border-b border-slate-200 px-5 last:border-0 ${
                              rank === 1 ? 'bg-amber-50/70 ring-1 ring-inset ring-amber-300' : 'bg-white'
                            }`}
                          >
                            <div>
                              {rank === 1 ? (
                                <span className="grid size-10 place-items-center rounded-full bg-amber-100 text-amber-700 shadow-sm">
                                  <Crown className="size-6 fill-amber-400 text-amber-500" />
                                </span>
                              ) : (
                                <span className="grid size-9 place-items-center rounded-lg border border-slate-200 text-sm font-black text-slate-700">{formatFaNumber(rank)}</span>
                              )}
                            </div>
                            <div className="flex min-w-0 items-center gap-3">
                              <TeamAvatar avatarId={team.avatarId} color={team.color} className="size-11 rounded-[15px]" />
                              <div className="min-w-0">
                                <b className="block truncate text-base font-black text-[#172033]" dir="auto">{team.name}</b>
                                <span className="mt-1 inline-flex rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold text-slate-500">
                                  {teamPerformanceLabel(team)}
                                </span>
                              </div>
                            </div>
                            <motion.strong key={team.score} initial={{ scale: 1.18, color: '#F26A21' }} animate={{ scale: 1, color: '#172033' }} className="text-xl font-black">
                              {formatFaNumber(team.score)}
                            </motion.strong>
                            <span className="font-black text-emerald-600">
                              {formatFaNumber(team.correctCount)} / {formatFaNumber(TEAM_QUESTION_COUNT)}
                            </span>
                            <span className="flex items-center gap-2">
                              <span className="relative grid size-8 place-items-center rounded-full bg-slate-100">
                                <span className="absolute inset-0 rounded-full" style={{ background: `conic-gradient(#22C55E ${accuracy}%, #E5E7EB 0)` }} />
                                <i className="relative size-5 rounded-full bg-white" />
                              </span>
                              <b className="text-sm text-slate-700">{formatFaNumber(accuracy)}٪</b>
                            </span>
                            <span className="text-sm font-black text-slate-700">
                              {formatDuration(averageMs)}
                              <small className={`mt-1 block w-fit rounded-md px-2 py-0.5 text-[10px] ${averageMs <= 60_000 ? 'bg-emerald-50 text-emerald-600' : averageMs <= 120_000 ? 'bg-amber-50 text-amber-600' : 'bg-orange-50 text-orange-600'}`}>
                                {averageMs <= 60_000 ? 'سریع' : averageMs <= 120_000 ? 'متوسط' : 'کند'}
                              </small>
                            </span>
                            <span className="text-xs leading-6 text-slate-500">
                              {activityTime(team.updatedAtMs)}
                              <small className="block text-[10px] text-slate-400">{team.status === 'playing' ? 'همین حالا' : team.status === 'finished' ? 'کامل شده' : 'منتظر شروع'}</small>
                            </span>
                            <span className={`flex items-center gap-1 font-black ${delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                              {delta > 0 ? <ArrowUp className="size-4" /> : delta < 0 ? <ArrowDown className="size-4" /> : <Minus className="size-4" />}
                              {delta ? formatFaNumber(Math.abs(delta)) : '—'}
                            </span>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  </motion.div>
                </div>
              </div>

              {!teams.length && (
                <div className="grid h-[45vh] place-items-center text-center text-slate-400">
                  <div>
                    <Database className="mx-auto mb-4 size-9" />
                    هنوز تیمی وارد میدان نشده است.
                  </div>
                </div>
              )}
            </div>

            <footer className="flex h-16 items-center justify-between border-t border-slate-200 px-7 text-sm text-slate-600">
              <div className="flex items-center gap-2" dir="ltr">
                <button type="button" className="grid size-9 place-items-center rounded-lg border border-slate-200 text-slate-500">‹</button>
                <button type="button" className="grid size-9 place-items-center rounded-lg bg-brand font-black text-white">۱</button>
                <button type="button" className="grid size-9 place-items-center rounded-lg border border-slate-200 text-slate-500">۲</button>
                <span className="px-2 text-slate-400">...</span>
                <button type="button" className="grid size-9 place-items-center rounded-lg border border-slate-200 text-slate-500">›</button>
              </div>
              <div className="flex items-center gap-4">
                <span>تیم در هر صفحه</span>
                <button type="button" className="inline-flex h-10 items-center gap-3 rounded-xl border border-slate-200 px-4 font-bold">
                  ۱۰
                  <ChevronDown className="size-4" />
                </button>
                <span>نمایش</span>
              </div>
            </footer>
          </div>
        </section>

        <aside className="hidden min-h-screen bg-[#172033] p-5 text-white lg:flex lg:flex-col" dir="rtl">
          <div className="px-3 py-3">
            <BrandLockup inverse />
          </div>

          <div className="mt-6 flex items-center gap-3 rounded-2xl bg-white/[.065] p-3">
            <TeamAvatar avatarId="rank-strategist" color="#F26A21" className="size-12 rounded-full" />
            <div className="min-w-0 flex-1">
              <b className="block truncate text-sm">مدیر مسابقه</b>
              <span className="text-xs text-white/50">Administrator</span>
            </div>
            <Settings className="size-4 text-white/50" />
          </div>

          <nav className="mt-8 space-y-2 text-sm text-white/60">
            <SideItem icon={<BarChart3 className="size-5" />} label="داشبورد" />
            <SideItem icon={<Database className="size-5" />} label="مدیریت مسابقه" muted />
            <SideItem icon={<Users className="size-5" />} label="شرکت‌کنندگان" />
            <SideItem icon={<BarChart3 className="size-5" />} label="جدول رتبه‌بندی زنده" active />
            <SideItem icon={<HelpCircle className="size-5" />} label="سؤالات" />
            <SideItem icon={<ClipboardList className="size-5" />} label="داوری و نمره‌دهی" />
            <SideItem icon={<Bell className="size-5" />} label="اعلان‌ها" />
            <SideItem icon={<FileSpreadsheet className="size-5" />} label="گزارش‌ها" />
            <SideItem icon={<Settings className="size-5" />} label="تنظیمات" muted />
          </nav>

          <div className="mt-auto space-y-4">
            <SideItem icon={<LogOut className="size-5" />} label="خروج" />
            <div className="rounded-3xl border border-white/10 bg-white/[.045] p-5 text-center">
              <Trophy className="mx-auto size-16 fill-gold/20 text-gold" />
              <b className="mt-4 block">DataBinex Arena</b>
              <span className="mt-2 block text-xs leading-6 text-white/45">قدرت داده، مهارت تحلیل</span>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}

function FilterButton({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <button type="button" className="flex h-12 items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm">
      <span className="inline-flex items-center gap-2">
        {icon}
        {label}
      </span>
      <ChevronDown className="size-4 text-slate-400" />
    </button>
  );
}

function SideItem({ icon, label, active = false, muted = false }: { icon: ReactNode; label: string; active?: boolean; muted?: boolean }) {
  return (
    <div className={`flex h-12 items-center justify-between rounded-xl px-4 font-bold transition ${
      active ? 'bg-brand text-white shadow-[0_14px_36px_rgba(242,106,33,.32)]' : muted ? 'text-white/45' : 'text-white/62'
    }`}>
      <span>{label}</span>
      {icon}
    </div>
  );
}

function activityTime(updatedAtMs: number) {
  if (!updatedAtMs) return '—';
  const diff = Date.now() - updatedAtMs;
  if (diff < 60_000) return new Date(updatedAtMs).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const minutes = Math.round(diff / 60_000);
  return `${formatFaNumber(minutes)} دقیقه پیش`;
}

function displayEventName(name: string | undefined) {
  if (!name || /DataBinex\s+T-SQL\s+Arena/i.test(name)) return 'DataBinex Arena';
  return name;
}

function FinalLeaderboard({ teams }: { teams: TeamPublic[] }) {
  const [winner, second, third] = teams;
  const totalSolved = teams.reduce((sum, team) => sum + team.correctCount, 0);
  const totalQuestions = Math.max(1, teams.length * TEAM_QUESTION_COUNT);
  const solvedTeams = teams.filter((team) => team.correctCount > 0 && team.score > 0);
  const hasAwardableCompetition = teams.length > 1 && solvedTeams.length > 0;
  const fastestTeam = hasAwardableCompetition ? [...solvedTeams].sort((a, b) => averageAnswerMs(a) - averageAnswerMs(b))[0] : undefined;
  const mostAccurate = hasAwardableCompetition ? [...solvedTeams].sort((a, b) => (b.correctCount / Math.max(1, b.submittedCount)) - (a.correctCount / Math.max(1, a.submittedCount)))[0] : undefined;
  const averageTime = solvedTeams.length ? solvedTeams.reduce((sum, team) => sum + averageAnswerMs(team), 0) / solvedTeams.length : 0;
  return (
    <main className="dbx-aurora arena-grid min-h-screen overflow-hidden px-6 py-5 text-white">
      <header className="mx-auto flex max-w-7xl items-center justify-between border-b border-white/8 pb-4">
        <div className="flex items-center gap-5">
          <span className="rounded-2xl border border-white/10 bg-white/[.055] px-4 py-2 text-xs text-white/60">تیم‌ها: {formatFaNumber(teams.length)}</span>
        </div>
        <nav className="hidden items-center gap-8 text-sm text-white/64 md:flex">
          <span>DataBinex Arena</span><span className="border-b-2 border-brand pb-2 text-white">جدول امتیازات</span><span>تحلیل مسابقه</span>
        </nav>
        <BrandLockup inverse />
      </header>
      <section className="mx-auto mt-5 max-w-7xl text-center">
        <div className="relative mx-auto h-24 w-36">
          <Trophy className="absolute left-1/2 top-0 size-24 -translate-x-1/2 fill-gold/25 text-gold drop-shadow-[0_0_42px_rgba(248,185,49,.36)]" />
        </div>
        <h1 className="mt-1 text-4xl font-black md:text-5xl">نتایج نهایی DataBinex Arena</h1>
        <p className="mt-3 text-lg text-white/58">{hasAwardableCompetition ? 'نتیجه رقابت بر اساس Queryهای درست و زمان پاسخ ثبت شد.' : 'گزارش مسابقه ثبت شد؛ برای اعلام برنده یا لقب عملکرد، داده کافی وجود نداشت.'}</p>
        <div className="mt-8 grid items-end gap-6 lg:grid-cols-3">
          <PodiumCard team={second} rank={2} />
          <PodiumCard team={winner} rank={1} featured />
          <PodiumCard team={third} rank={3} />
        </div>
        <div className="mt-5 overflow-hidden rounded-[26px] border border-white/10 bg-white/[.075] text-right shadow-[0_24px_80px_rgba(0,0,0,.22)] backdrop-blur-xl">
          <div className="grid grid-cols-[80px_1fr_150px_150px_150px] border-b border-white/10 bg-white/[.035] px-5 py-4 text-[10px] font-bold text-white/45"><span>رتبه</span><span>تیم</span><span>امتیاز کل</span><span>درصد صحت</span><span>زمان کل</span></div>
          {teams.slice(3, 10).map((team, index) => (
            <div key={team.id} className="grid grid-cols-[80px_1fr_150px_150px_150px] items-center border-b border-white/8 px-5 py-4 last:border-0">
              <b>{formatFaNumber(index + 4)}</b>
              <span className="flex items-center gap-3"><TeamAvatar avatarId={team.avatarId} color={team.color} className="size-9 rounded-xl" />{team.name}</span>
              <b>{formatFaNumber(team.score)}</b>
              <span>{formatFaNumber(Math.round((team.correctCount / Math.max(1, team.submittedCount)) * 100))}٪</span>
              <span>{formatDuration(averageAnswerMs(team))}</span>
            </div>
          ))}
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-5">
          <FinalMetric icon={<ClipboardList />} label="سؤالات حل‌شده" value={`${formatFaNumber(totalSolved)} / ${formatFaNumber(totalQuestions)}`} detail="توسط همه تیم‌ها" tone="cyan" />
          <FinalMetric icon={<Trophy />} label="مدت زمان مسابقه" value="۴۰:۰۰" detail="زمان رسمی رقابت" tone="orange" />
          <FinalMetric icon={<Clock />} label="میانگین زمان حل" value={formatDuration(averageTime)} detail="فقط پاسخ‌های درست" tone="purple" />
          <FinalMetric icon={<Target />} label="دقیق‌ترین تیم" value={mostAccurate?.name ?? '—'} detail={mostAccurate ? `${formatFaNumber(Math.round((mostAccurate.correctCount / Math.max(1, mostAccurate.submittedCount)) * 100))}٪` : '—'} tone="blue" />
          <FinalMetric icon={<Rocket />} label="سریع‌ترین تیم" value={fastestTeam?.name ?? '—'} detail={fastestTeam ? formatDuration(averageAnswerMs(fastestTeam)) : '—'} tone="green" />
        </div>
        <button type="button" onClick={() => window.print()} className="mt-4 rounded-xl bg-brand px-7 py-3 text-sm font-black text-white shadow-[0_18px_48px_rgba(242,106,33,.3)]">
          دانلود گزارش نتایج
        </button>
      </section>
    </main>
  );
}

function PodiumCard({ team, rank, featured = false }: { team: TeamPublic | undefined; rank: number; featured?: boolean }) {
  if (!team) return <div />;
  return (
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className={`relative rounded-[28px] border p-7 ${featured ? 'border-gold/60 bg-white/[.12] shadow-[0_0_80px_rgba(248,185,49,.16)] lg:pb-12' : 'border-white/15 bg-white/[.07]'}`}>
      <span className={`absolute -top-5 left-1/2 grid size-11 -translate-x-1/2 place-items-center rounded-full font-black ${rank === 1 ? 'bg-gold text-ink' : 'bg-white text-slate-600'}`}>{formatFaNumber(rank)}</span>
      <TeamAvatar avatarId={team.avatarId} color={team.color} className="mx-auto mt-4 size-20 rounded-[24px] text-2xl" />
      <h2 className="mt-5 text-2xl font-black">{team.name}</h2>
      <p className="mt-2 text-sm text-white/45">{formatFaNumber(team.score)} امتیاز</p>
      <p className="mt-2 text-white/65">{formatDuration(averageAnswerMs(team))}</p>
    </motion.div>
  );
}

function FinalMetric({ icon, label, value, detail, tone }: { icon: ReactNode; label: string; value: string; detail: string; tone: 'cyan' | 'orange' | 'purple' | 'blue' | 'green' }) {
  const tones = {
    cyan: 'border-cyan/35 text-cyan',
    orange: 'border-orange-400/40 text-orange-300',
    purple: 'border-purple-400/40 text-purple-300',
    blue: 'border-brand/45 text-brand',
    green: 'border-emerald-400/40 text-emerald-300',
  } as const;
  return (
    <div className="rounded-[20px] border border-white/10 bg-white/[.07] p-4 text-right backdrop-blur-xl">
      <div className="flex items-center gap-3">
        <span className={`grid size-14 place-items-center rounded-full border bg-white/[.035] ${tones[tone]}`}>{icon}</span>
        <div className="min-w-0">
          <span className="block text-xs font-bold text-white/60">{label}</span>
          <strong className="mt-1 block truncate text-xl">{value}</strong>
        </div>
      </div>
      <span className="mt-3 block text-[11px] text-white/40">{detail}</span>
    </div>
  );
}
